
import React, { useState, useEffect } from 'react';
import AddressAutocomplete from '@/components/ui-custom/AddressAutocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useLeads, ServiceType } from '@/context/LeadContext';
import { usePartners } from '@/context/PartnerContext';
import { toast } from '@/components/ui/use-toast';
import ThankYouMessage from '@/components/ui-custom/ThankYouMessage';
import { calculateDistance } from '@/utils/geoUtils';
import { Bolt, CheckCircle2, Clock3, Flame, Home, Leaf, MapPin, MessageSquare, Phone, ShieldCheck, Snowflake, Sun, User, Wind } from 'lucide-react';

// Import the Partner type from PartnerContext
import type { Partner } from '@/context/PartnerContext';

interface GuestFormProps {
  onSubmitSuccess?: () => void;
}

const serviceOptions: Array<{
  value: ServiceType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  cardClass: string;
  iconClass: string;
}> = [
  {
    value: 'εξοικονομώ',
    label: 'Εξοικονομώ',
    icon: Home,
    cardClass: 'border-blue-200 bg-blue-50/70',
    iconClass: 'text-blue-600'
  },
  {
    value: 'αντλίεςΘερμότητας',
    label: 'Αντλίες Θερμότητας',
    icon: Wind,
    cardClass: 'border-cyan-200 bg-cyan-50/70',
    iconClass: 'text-cyan-600'
  },
  {
    value: 'φωτοβολταϊκά',
    label: 'Φωτοβολταϊκά',
    icon: Sun,
    cardClass: 'border-amber-200 bg-amber-50/70',
    iconClass: 'text-amber-600'
  },
  {
    value: 'κλιματιστικά',
    label: 'Κλιματιστικά',
    icon: Snowflake,
    cardClass: 'border-indigo-200 bg-indigo-50/70',
    iconClass: 'text-indigo-600'
  },
  {
    value: 'φυσικόΑέριο',
    label: 'Φυσικό Αέριο',
    icon: Flame,
    cardClass: 'border-rose-200 bg-rose-50/70',
    iconClass: 'text-rose-600'
  },
  {
    value: 'ηλιακά',
    label: 'Ηλιακά',
    icon: Leaf,
    cardClass: 'border-emerald-200 bg-emerald-50/70',
    iconClass: 'text-emerald-600'
  },
  {
    value: 'άλλο',
    label: 'Άλλο',
    icon: Bolt,
    cardClass: 'border-slate-200 bg-slate-50/70',
    iconClass: 'text-slate-600'
  },
];

export default function GuestForm({ onSubmitSuccess }: GuestFormProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [service, setService] = useState<ServiceType>('εξοικονομώ');
  const [timeframe, setTimeframe] = useState('');
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const { addLead } = useLeads();
  const { partners } = usePartners();

  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  const handleLocationSelect = (lat: number, lng: number, postal: string) => {
    setLatitude(lat);
    setLongitude(lng);
    setPostalCode(postal);
  };

  // Check any partners are within range of the location
  const checkPartnersInRange = (latitude: number, longitude: number, service: ServiceType) => {
    if (!latitude || !longitude) return false;

    const partnersWithLocation = partners.filter(
      p => p.active && p.services.includes(service) && p.latitude && p.longitude && p.radius
    );

    return partnersWithLocation.some(partner => {
      const distance = calculateDistance(latitude, longitude, partner.latitude!, partner.longitude!);
      return distance <= (partner.radius || 10);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !phone || !address || !postalCode || !service || !timeframe) {
      toast({
        title: 'Σφάλμα',
        description: 'Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const newLead = await addLead(name, phone, address, postalCode, service, timeframe, comments, latitude, longitude);

      if (newLead) {
        setName('');
        setPhone('');
        setAddress('');
        setPostalCode('');
        setService('εξοικονομώ');
        setTimeframe('');
        setComments('');
        setLatitude(null);
        setLongitude(null);
        setShowMessage(true);

        // Call the onSubmitSuccess prop if it exists
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }

        const hasPartnersInRange = checkPartnersInRange(latitude || 0, longitude || 0, service);

        if (!hasPartnersInRange) {
          toast({
            title: 'Επιτυχία',
            description: 'Η φόρμα υποβλήθηκε με επιτυχία!',
          });
        } else {
          toast({
            title: 'Επιτυχία',
            description: 'Η φόρμα υποβλήθηκε με επιτυχία!',
          });
        }
      } else {
        toast({
          title: 'Σφάλμα',
          description: 'Υπήρξε ένα σφάλμα κατα την υποβολή, παρακαλώ προσπαθήστε ξανά.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: 'Σφάλμα',
        description: 'Υπήρξε ένα σφάλμα κατα την υποβολή, παρακαλώ προσπαθήστε ξανά.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-1 py-2 sm:px-2">
      {showMessage ? (
        <ThankYouMessage />
      ) : (
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg bg-emerald-100 p-2">
                <ShieldCheck className="h-4 w-4 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Δωρεάν εκτίμηση από ειδικό συνεργάτη</p>
                <p className="text-xs text-slate-600 mt-1">Η υποβολή διαρκεί λιγότερο από 1 λεπτό και επικοινωνούμε άμεσα.</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <p className="text-sm font-semibold text-slate-900 mb-3">1. Επίλεξε υπηρεσία</p>
            <div className="grid grid-cols-2 gap-3">
              {serviceOptions.map((option) => {
                const Icon = option.icon;
                const isSelected = service === option.value;
                const isOtherOption = option.value === 'άλλο';

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setService(option.value)}
                    className={`group rounded-2xl border px-3 text-center transition-all flex flex-col items-center justify-center relative ${
                      isOtherOption ? 'col-span-2 min-h-[84px] py-2.5' : 'min-h-[124px] py-4'
                    } ${
                      isSelected
                        ? `${option.cardClass} ring-2 ring-offset-0 ring-primary/35 border-primary/40 shadow-sm`
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {isSelected && <CheckCircle2 className="h-4 w-4 text-emerald-600 absolute right-3 top-3" />}
                    <div className={`rounded-xl p-2.5 ${isSelected ? 'bg-white/85' : 'bg-slate-100'}`}>
                      <Icon className={`h-7 w-7 ${isSelected ? option.iconClass : 'text-slate-500'}`} />
                    </div>
                    <div className="text-base font-semibold text-slate-900 leading-tight mt-2">
                      {option.label}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2">Συμπληρώστε στα σχόλια.</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-5">
            <p className="text-sm font-semibold text-slate-900">2. Συμπλήρωσε στοιχεία επικοινωνίας</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-500" />
                  Όνομα *
                </label>
                <Input
                  type="text"
                  id="name"
                  placeholder="Π.χ. Γιάννης Παπαδόπουλος"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 bg-slate-50 border-slate-200 rounded-xl focus-visible:ring-primary/30"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-500" />
                  Τηλέφωνο *
                </label>
                <Input
                  type="tel"
                  id="phone"
                  inputMode="tel"
                  placeholder="Π.χ. 69XXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-12 bg-slate-50 border-slate-200 rounded-xl focus-visible:ring-primary/30"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="address" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-500" />
                Διεύθυνση *
              </label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onLocationSelect={handleLocationSelect}
                placeholder="Πληκτρολογήστε διεύθυνση"
              />
              <p className="text-xs text-slate-500">Η σωστή τοποθεσία βοηθά στην ταχύτερη ανάθεση συνεργάτη.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="postalCode" className="text-sm font-semibold text-slate-800">ΤΚ *</label>
                <Input
                  type="text"
                  id="postalCode"
                  inputMode="numeric"
                  placeholder="Π.χ. 11527"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="h-12 bg-slate-50 border-slate-200 rounded-xl focus-visible:ring-primary/30"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="timeframe" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Clock3 className="h-4 w-4 text-slate-500" />
                  Χρονικό πλαίσιο *
                </label>
                <Select value={timeframe} onValueChange={setTimeframe}>
                  <SelectTrigger className="h-12 bg-slate-50 border-slate-200 rounded-xl focus:ring-primary/30">
                    <SelectValue placeholder="Πότε θέλετε να ξεκινήσει;" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="άμεσα">Άμεσα</SelectItem>
                    <SelectItem value="εντός3μηνών">Εντός 3 μηνών</SelectItem>
                    <SelectItem value="κάνωΈρευνα">Κάνω έρευνα αγοράς</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="comments" className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-slate-500" />
                Σχόλια (προαιρετικά)
              </label>
              <Textarea
                id="comments"
                placeholder="Γράψτε λεπτομέρειες που θα βοηθήσουν την προσφορά"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl min-h-[110px] focus-visible:ring-primary/30"
              />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600" />
            Τα στοιχεία χρησιμοποιούνται αποκλειστικά για την επικοινωνία σχετικά με το αίτημά σας.
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold">
            {isSubmitting ? 'Υποβολή...' : 'Λήψη δωρεάν προσφοράς'}
          </Button>
        </form>
      )}
    </div>
  );
}
